import React, { useEffect, useState, useCallback } from 'react';
import { detectOpening, translateSanToSpanish } from '@chess-platform/shared';
import { RotateCcw, Flag, Terminal, Search, GraduationCap, Brain, BookOpen, Sparkles, AlertTriangle, ChevronLeft, ChevronRight, Play as PlayIcon, Square } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useGameLogic } from '../modules/game/useGameLogic';
import { usePlayEngine } from '../modules/game/hooks/usePlayEngine';
import ChessBoard from '../components/ChessBoard';
import { useChessClock } from '../hooks/useChessClock';
import GameConfigModal from '../components/GameConfigModal';
import ChatPanel from '../components/ChatPanel';
import CoachPanel from '../components/CoachPanel';
import { useTranslation } from '../hooks/useTranslation';
import { useSocket } from '../hooks/useSocket';
import { useAIFeedback } from '../hooks/useAIFeedback';
import ConfirmModal from '../components/ConfirmModal';
import { useMaterialAdvantage } from '../hooks/useMaterialAdvantage';
import CapturedPieces from '../components/CapturedPieces';
import GuestRestrictionModal from '../components/GuestRestrictionModal';

const PlayPage: React.FC = () => {
    const { user, isGuest } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [gameId, setGameId] = useState<string | null>(null);
    const [isMultiplayer, setIsMultiplayer] = useState(false);
    const [showMobileAnalysis, setShowMobileAnalysis] = useState(false);
    const [reviewIdx, setReviewIdx] = useState<number | null>(null);
    const [confirmOpts, setConfirmOpts] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
    const [restrictionFeature, setRestrictionFeature] = useState<string | null>(null);

    // AI Coach states
    const [coachExplanation, setCoachExplanation] = useState<any | null>(null);
    const [aiProvider, setAiProvider] = useState<string>('openai');
    const [aiApiKey, setAiApiKey] = useState<string>('');

    const { getExplanation, loading: coachLoading } = useAIFeedback();

    const logic = useGameLogic();
    const {
        gameState,
        setGameState,
        playerColor,
        fen,
        turn,
        isGameOver,
        surrendered,
        status,
        history,
        timeWInitial,
        timeBInitial,
        timeControlStr,
        isEngineReady,
        startGame,
        handlePlayerMove,
        surrender,
        timeOut,
        triggerCpuMove,
        difficultyLevel,
        uciHistory,
        fenHistory,
        loadGame,
        handleRemoteMove
    } = logic;

    const location = useLocation();

    const {
        timeW,
        timeB,
        setTimeW,
        setTimeB,
        formatTime
    } = useChessClock({
        timeWInitial,
        timeBInitial,
        turn,
        isGameOver: isGameOver || surrendered,
        isActive: history.length > 0,
        onTimeOut: timeOut
    });

    const {
        evaluation,
        reviewHistory,
        isCalculating,
        currentOpening
    } = usePlayEngine(fen, history, uciHistory, fenHistory, isEngineReady, !isMultiplayer, playerColor);

    const { isConnected, emit, on, socket } = useSocket(gameId || undefined);

    // Resets coach explanation if user navigates back in history
    useEffect(() => {
        setCoachExplanation(null);
    }, [reviewIdx, fen]);

    // When game ends, reset reviewIdx to last position so navigation starts from the end
    useEffect(() => {
        if (isGameOver && reviewIdx === null) {
            setReviewIdx(history.length - 1);
        }
        if (!isGameOver) {
            setReviewIdx(null);
        }
    }, [isGameOver, history.length]);

    // The review shown in the panel: during game → always the latest; post-game → follows reviewIdx
    const activeReviewIdx = isGameOver && reviewIdx !== null ? reviewIdx : history.length - 1;
    const lastReview = reviewHistory[activeReviewIdx] || (history.length > 1 ? reviewHistory[history.length - 2] : null);

    // The board FEN: during game → live; post-game → historical position
    const historyFen = isGameOver && reviewIdx !== null && fenHistory[reviewIdx + 1]
        ? fenHistory[reviewIdx + 1]
        : null;

    // Square highlights from the active review
    const highlightSquares: Record<string, React.CSSProperties> = {};
    if (lastReview?.ui?.suggestedHighlights) {
        for (const sq of lastReview.ui.suggestedHighlights) {
            highlightSquares[sq] = {
                background: 'rgba(251, 191, 36, 0.35)',
                boxShadow: 'inset 0 0 0 2px rgba(251, 191, 36, 0.7)',
            };
        }
    }

    const getBadgeConfig = (label?: string) => {
        if (!label) return { color: 'text-white/80', symbol: '', bg: '', hex: '' };
        const labels: Record<string, { color: string; symbol: string; bg: string; hex: string }> = {
            brilliant: { color: 'text-white', symbol: '!!', bg: 'bg-indigo-600', hex: '#6366f1' },
            great: { color: 'text-white', symbol: '!', bg: 'bg-indigo-500', hex: '#6366f1' },
            best: { color: 'text-white', symbol: '*', bg: 'bg-indigo-400', hex: '#6366f1' },
            excellent: { color: 'text-white', symbol: '', bg: 'bg-indigo-400', hex: '#6366f1' },
            good: { color: 'text-slate-900', symbol: '', bg: 'bg-slate-100', hex: '#f1f5f9' },
            book: { color: 'text-white', symbol: '📖', bg: 'bg-[#8B4513]', hex: '#8B4513' }, // Marrón: Siempre visible
            inaccuracy: { color: 'text-slate-900', symbol: '?!', bg: isGameOver ? 'bg-yellow-400' : 'bg-slate-100', hex: '#facc15' },
            mistake: { color: 'text-white', symbol: '?', bg: isGameOver ? 'bg-orange-500' : 'bg-slate-100', hex: '#f97316' },
            blunder: { color: 'text-white', symbol: '??', bg: isGameOver ? 'bg-red-500' : 'bg-slate-100', hex: '#ef4444' },
            forced: { color: 'text-white/40', symbol: '□', bg: '', hex: '' },
        };
        return labels[label] || { color: 'text-white/80', symbol: '', bg: '', hex: '' };
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const online = query.get('online');
        const id = query.get('id');

        if (online === 'true' && id) {
            if (isGuest) {
                setRestrictionFeature('Juego Online Multijugador');
                // Quitamos el flag de online de la URL para no buclear
                navigate('/play', { replace: true });
                return;
            }

            if (user) {
                setIsMultiplayer(true);
                setGameId(id);
                api.get(`/games/${id}`).then(res => {
                    const game = res.data;
                    const isWhite = game.whitePlayerId === user.id;
                    loadGame({
                        fen: game.fen,
                        playerColor: isWhite ? 'w' : 'b',
                        history: game.moves.map((m: any) => m.san),
                        uciHistory: game.moves.map((m: any) => m.uci),
                        isGameOver: game.status !== 'playing',
                        status: game.status === 'playing' ? (game.turn === (isWhite ? 'w' : 'b') ? 'TU TURNO' : 'OPONENTE PENSANDO...') : game.result,
                        timeControl: game.timeControl
                    });
                }).catch(err => {
                    console.error('Error loading multiplayer game:', err);
                    navigate('/dashboard');
                });
            }
        }
    }, [location.search, user, isGuest, loadGame, navigate]);

    useEffect(() => {
        const savedProvider = localStorage.getItem('chess_ai_provider');
        const savedKey = localStorage.getItem('chess_ai_key');
        if (savedProvider) setAiProvider(savedProvider);
        if (savedKey) setAiApiKey(savedKey);
    }, []);

    const handleAskCoach = async () => {
        if (!lastReview) return;

        const explanation = await getExplanation({
            evidence: lastReview,
            provider: aiProvider,
            apiKey: aiApiKey || undefined
        });

        if (explanation) setCoachExplanation(explanation);
    };

    useEffect(() => {
        if (gameState !== 'playing' || !user || isMultiplayer) return;
        api.post('/games', {
            fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
            whitePlayerId: playerColor === 'w' ? user.id : undefined,
            blackPlayerId: playerColor === 'b' ? user.id : undefined,
            source: 'ai',
            aiLevel: difficultyLevel,
            timeControl: timeControlStr
        }).then(res => setGameId(res.data.id)).catch(() => { });
    }, [gameState, user, playerColor, difficultyLevel, timeControlStr, isMultiplayer]);

    const handlePlayerMoveInput = (move: { from: string; to: string; promotion?: string }) => {
        const res = handlePlayerMove(move);
        if (res && gameId) {
            if (isMultiplayer) {
                console.log('[PlayPage] EMITIENDO movimiento:', { gameId, move, uci: res.moveUci });
                emit('move', { gameId, move, fen: res.newFen, timeW, timeB });
            }
            console.log('[PlayPage] Guardando movimiento en DB:', res.moveUci);
            api.put(`/games/${gameId}`, { fen: res.newFen }).catch(() => { });
            api.post(`/games/${gameId}/moves`, {
                moveNumber: res.moveNumber,
                fen: res.newFen,
                san: res.san,
                uci: res.moveUci
            }).catch(() => { });
        }
    };

    const handleSurrender = () => {
        setConfirmOpts({
            title: 'Rendirse',
            message: '¿Seguro que deseas rendirte?',
            onConfirm: () => {
                surrender();
                if (isMultiplayer && gameId && isConnected) {
                    emit('resign', { gameId });
                    api.put(`/games/${gameId}`, { status: 'resigned', result: playerColor === 'w' ? 'black_won' : 'white_won' }).catch(() => { });
                }
                setConfirmOpts(null);
            }
        });
    };

    useEffect(() => {
        if (gameState !== 'playing' || turn === playerColor || isMultiplayer) return;

        triggerCpuMove().then((res: any) => {
            if (res && gameId) {
                api.put(`/games/${gameId}`, { fen: res.newFen }).catch(() => { });
                api.post(`/games/${gameId}/moves`, {
                    moveNumber: res.moveNumber,
                    fen: res.newFen,
                    san: res.san,
                    uci: res.moveUci
                }).catch(() => { });
            }
        });
    }, [turn, playerColor, gameState, isEngineReady, triggerCpuMove, gameId, isMultiplayer]);

    useEffect(() => {
        if (!isMultiplayer) return;
        console.log('[PlayPage] Configurando listeners de Socket.IO para el juego:', gameId);

        const moveCleanup = on('move_made', (data: { move: any; fen: string; timeW: number; timeB: number }) => {
            console.log('[PlayPage] RECIBIDO movimiento del oponente:', data);
            const uci = typeof data.move === 'string'
                ? data.move
                : `${data.move.from}${data.move.to}${data.move.promotion || ''}`;

            handleRemoteMove(uci);

            if (data.timeW !== undefined) setTimeW(data.timeW);
            if (data.timeB !== undefined) setTimeB(data.timeB);
        });

        const syncCleanup = on('clock_sync', (data: { timeW?: number; timeB?: number; turn?: string }) => {
            if (data.timeW !== undefined) setTimeW(data.timeW);
            if (data.timeB !== undefined) setTimeB(data.timeB);
        });

        return () => {
            moveCleanup();
            syncCleanup();
        };
    }, [isMultiplayer, on, handleRemoteMove, setTimeW, setTimeB]);

    useEffect(() => {
        if (!isMultiplayer) return;

        const resignCleanup = on('opponent_resigned', () => {
            setGameState('playing');
            surrender();
            loadGame({
                fen, playerColor, history, uciHistory,
                isGameOver: true,
                status: `VICTORIA - OPONENTE ABANDONÓ`
            });
        });

        return () => {
            resignCleanup();
        };
    }, [isMultiplayer, on, surrender, loadGame, fen, playerColor, history, uciHistory]);

    // Periodic clock sync
    useEffect(() => {
        if (!isMultiplayer || !gameId || !isConnected) return;

        const interval = setInterval(() => {
            emit('request_clock_sync', { gameId });
        }, 10000); // Every 10 seconds

        return () => clearInterval(interval);
    }, [isMultiplayer, gameId, isConnected, emit]);

    // Compute board Fen for UI and Hooks
    const boardFen = historyFen
        ? historyFen
        : (fen === 'start' ? 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' : fen);

    // Call hooks BEFORE any early returns!
    const { whiteAdvantage, blackAdvantage, capturedWhitePieces, capturedBlackPieces } = useMaterialAdvantage(boardFen);

    if (gameState === 'config') {
        return (
            <div className="flex justify-center items-center py-12 px-8 min-h-[calc(100dvh-100px)]">
                <GameConfigModal onStart={startGame} />
            </div>
        );
    }

    const opponentTime = playerColor === 'w' ? timeB : timeW;
    const myTime = playerColor === 'w' ? timeW : timeB;

    const myAdvantage = playerColor === 'w' ? whiteAdvantage : blackAdvantage;
    const opponentAdvantage = playerColor === 'w' ? blackAdvantage : whiteAdvantage;

    // The pieces MY OPPONENT captured are the ones I LOST (my color)
    const opponentCapturedPieces = playerColor === 'w' ? capturedWhitePieces : capturedBlackPieces;
    const opponentCapturedColor = playerColor === 'w' ? 'w' : 'b';

    // The pieces I CAPTURED are the ones MY OPPONENT LOST (opposite color)
    const myCapturedPieces = playerColor === 'w' ? capturedBlackPieces : capturedWhitePieces;
    const myCapturedColor = playerColor === 'w' ? 'b' : 'w';

    return (
        <div className="flex flex-1 flex-col lg:flex-row gap-4 h-full p-2 lg:p-8 animate-in fade-in duration-500 overflow-hidden bg-[#0a0c10] overscroll-none text-white">
            {/* LEFT: Board area */}
            <div className="flex-[3] flex flex-col gap-2 lg:gap-4 min-h-0 relative">
                {/* HUD + Board Center */}
                <div className="flex-1 flex flex-row items-center justify-center p-2 lg:p-4 min-h-0 overflow-hidden gap-4">
                    <div className="w-full max-w-[min(100%,60vh)] lg:max-w-[min(100%,65vh)] h-full max-h-[95vh] flex flex-col justify-center gap-3 lg:gap-4 relative px-2">
                        {/* Opponent HUD Area */}
                        <div className="flex justify-between items-center px-2 py-1 shrink-0">
                            <div className="flex items-center gap-2 lg:gap-3">
                                <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 shadow-lg">
                                    <Terminal size={16} className="lg:w-[18px]" />
                                </div>
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] lg:text-[10px] font-black uppercase text-white tracking-widest leading-none">
                                            {isMultiplayer ? 'Oponente' : `CPU NIVEL ${difficultyLevel}`}
                                        </span>
                                        {isMultiplayer && (
                                            <div title={isConnected ? 'Conectado' : 'Desconectado'} className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${isConnected ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 animate-pulse'}`} />
                                        )}
                                    </div>
                                    <span className="text-[8px] font-bold text-white/30 uppercase tracking-tighter mb-1 relative top-0.5">Oponente</span>
                                    <CapturedPieces pieces={opponentCapturedPieces} advantage={opponentAdvantage} color={opponentCapturedColor} />
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                {evaluation && (
                                    <div className="px-2 py-1 rounded bg-white/5 border border-white/10 flex items-center gap-2">
                                        <div className={`w-1 h-3 rounded-full ${evaluation.score && evaluation.score > 0 ? 'bg-white' : 'bg-white/30'}`} />
                                        <span className="text-[10px] font-mono font-bold text-white/60">
                                            {evaluation.isMate ? `#${evaluation.mateMoves}` : (evaluation.score ? (evaluation.score > 0 ? `+${evaluation.score.toFixed(1)}` : evaluation.score.toFixed(1)) : '0.0')}
                                        </span>
                                    </div>
                                )}
                                <div className={`px-3 py-1.5 lg:px-4 lg:py-2 rounded-xl border tabular-nums transition-all duration-300 min-w-[80px] lg:min-w-[90px] text-center ${turn !== playerColor ? 'bg-red-500 border-red-400 text-white shadow-[0_0_20px_rgba(239,68,68,0.3)] scale-105 z-10' : 'bg-white/5 border-white/10 text-white/30'}`}>
                                    <span className="text-sm lg:text-lg font-black font-mono tracking-wider">{formatTime(opponentTime)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ChessBoard */}
                        <div className="aspect-square relative shadow-[0_20px_60px_rgba(0,0,0,0.8)] rounded-2xl lg:rounded-3xl overflow-hidden ring-2 ring-white/[0.03] p-0.5 bg-[#1a1c24] flex-1 min-h-0">
                            <ChessBoard
                                fen={boardFen}
                                orientation={playerColor === 'w' ? 'white' : 'black'}
                                onMove={handlePlayerMoveInput}
                                draggable={turn === playerColor && !isGameOver && !surrendered && !historyFen}
                                customArrows={[
                                    ...(evaluation?.bestMove && !isGameOver ? [[evaluation.bestMove.slice(0, 2), evaluation.bestMove.slice(2, 4), 'rgba(99, 102, 241, 0.2)']] : []),
                                    ...(lastReview?.ui?.suggestedArrows?.map(a => [a.from, a.to, a.color]) || [])
                                ] as [string, string, string][]}
                                customSquareStyles={highlightSquares}
                            />
                        </div>

                        {/* Post-game history navigation */}
                        {isGameOver && history.length > 0 && (
                            <div className="flex items-center justify-center gap-3 shrink-0">
                                <button
                                    onClick={() => setReviewIdx(i => Math.max(0, (i ?? history.length - 1) - 1))}
                                    disabled={reviewIdx === 0}
                                    className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-15 transition-all"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <div className="text-[9px] font-black tabular-nums text-white/20 min-w-[60px] text-center">
                                    {reviewIdx !== null ? `${reviewIdx + 1} / ${history.length}` : `${history.length} / ${history.length}`}
                                </div>
                                <button
                                    onClick={() => setReviewIdx(i => Math.min(history.length - 1, (i ?? history.length - 1) + 1))}
                                    disabled={reviewIdx === history.length - 1 || reviewIdx === null}
                                    className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:bg-white/10 hover:text-white disabled:opacity-15 transition-all"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}

                        {/* Opening Name Banner */}
                        {currentOpening && !isGameOver && history.length > 0 && history.length <= 30 && (
                            <div className="flex items-center justify-center gap-2 shrink-0 animate-in fade-in duration-500">
                                <BookOpen size={10} className="text-indigo-400/60 shrink-0" />
                                <span className="text-[9px] font-black text-white/40 uppercase tracking-widest truncate">
                                    {currentOpening.name}
                                </span>
                            </div>
                        )}

                        {/* My HUD Area */}
                        <div className="flex justify-between items-center px-2 py-1 shrink-0">
                            <div className="flex items-center gap-2 lg:gap-3">
                                <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 shadow-lg">
                                    <GraduationCap size={16} className="lg:w-[18px]" />
                                </div>
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-[9px] lg:text-[10px] font-black uppercase text-white tracking-widest leading-none">
                                            {user ? (user.name || user.email) : 'INVITADO'}
                                        </span>
                                        {isMultiplayer && isConnected && (
                                            <span className="text-[8px] font-bold text-green-500/80 tracking-tighter animate-pulse">LIVE</span>
                                        )}
                                    </div>
                                    <span className="text-[8px] font-bold text-white/30 uppercase tracking-tighter mb-1 relative top-0.5">Jugador</span>
                                    <CapturedPieces pieces={myCapturedPieces} advantage={myAdvantage} color={myCapturedColor} />
                                </div>
                            </div>
                            <div className={`px-3 py-1.5 lg:px-4 lg:py-2 rounded-xl border tabular-nums transition-all duration-300 min-w-[80px] lg:min-w-[90px] text-center ${turn === playerColor ? 'bg-indigo-500 border-indigo-400 text-white shadow-[0_0_20px_rgba(99,102,241,0.3)] scale-105 z-10' : 'bg-white/5 border-white/10 text-white/30'}`}>
                                <span className="text-sm lg:text-lg font-black font-mono tracking-wider">{formatTime(myTime)}</span>
                            </div>
                        </div>
                    </div>


                </div>

                {/* MOBILE ACTIONS */}
                {!showMobileAnalysis && (
                    <div className="lg:hidden flex flex-col gap-3 px-4 pb-8 mt-auto">
                        <div className="flex gap-2">
                            <button onClick={() => {
                                setConfirmOpts({
                                    title: 'Salir',
                                    message: '¿Seguro que deseas salir de la partida?',
                                    onConfirm: () => {
                                        setGameState('config');
                                        setConfirmOpts(null);
                                    }
                                });
                            }} className="flex-1 py-5 rounded-2xl bg-white/5 border border-white/10 text-white/80 font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                                <RotateCcw size={16} /> Salir
                            </button>
                            <button onClick={handleSurrender} disabled={isGameOver} className="flex-1 py-5 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-20">
                                <Flag size={16} /> Rendirse
                            </button>
                        </div>
                        {currentOpening && (
                            <div className="text-center pb-2">
                                <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest animate-pulse">
                                    {currentOpening.name}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* RIGHT: Move List & Coach AI (M3) */}
            <div className={`
                flex-1 flex flex-col gap-4 lg:gap-4 w-full lg:w-[380px] 
                bg-[#0a0c10] lg:bg-transparent
                lg:flex overflow-hidden shrink-0 border-t lg:border-t-0 border-white/10
            `}>
                <div className="flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4 custom-scrollbar">
                    {/* MAESTRO VIRTUAL (M3 Panel) - Coach Card */}
                    <div className="bg-indigo-500/5 lg:bg-indigo-500/10 lg:rounded-2xl border lg:border-indigo-500/20 p-5 flex flex-col gap-3 relative overflow-hidden group shrink-0">
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <GraduationCap size={64} />
                        </div>
                        <div className="flex items-center gap-2 text-indigo-400">
                            <GraduationCap size={16} />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Maestro Virtual</span>
                        </div>

                        <div className="text-xs lg:text-[13px] text-white/90 leading-relaxed font-medium">
                            {isCalculating ? (
                                <div className="flex items-center gap-2 text-indigo-400 animate-pulse py-2">
                                    <div className="w-1 h-1 rounded-full bg-indigo-400 animate-ping" />
                                    {history.length % 2 === 0 ? 'El Maestro estudia tu respuesta...' : 'El Maestro prepara su análisis...'}
                                </div>
                            ) : (
                                lastReview ? (
                                    <CoachPanel
                                        evidence={lastReview}
                                        onAskCoach={handleAskCoach}
                                        coachExplanation={coachExplanation}
                                        coachLoading={coachLoading}
                                    />
                                ) : (
                                    <div className="text-white/40 italic py-2">Mueve para comenzar la partida.</div>
                                )
                            )}
                        </div>

                        {lastReview && isGameOver && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-2 ${getBadgeConfig(lastReview.quality.label).bg} ${getBadgeConfig(lastReview.quality.label).color}`}>
                                    {lastReview.quality.label} {getBadgeConfig(lastReview.quality.label).symbol}
                                </span>
                            </div>
                        )}

                        {isGameOver && (
                            <button
                                onClick={() => {
                                    if (isGuest) {
                                        setRestrictionFeature('Análisis de Partida Completo');
                                    } else {
                                        navigate('/analysis');
                                    }
                                }}
                                className="mt-2 py-3 w-full rounded-xl bg-indigo-500 text-white font-black text-[10px] uppercase tracking-widest hover:bg-indigo-400 transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                                <Brain size={14} /> Analizar Partida Completa
                            </button>
                        )}
                    </div>

                    {/* TEORÍA DE APERTURA (Same Model as Analysis) */}
                    {(lastReview?.opening || currentOpening) && (
                        <div className="bg-[#1a1c24]/50 lg:rounded-2xl border border-white/5 p-5 shadow-xl shrink-0 animate-in slide-in-from-bottom-2 duration-300">
                            <div className="flex items-center gap-2 mb-3">
                                <BookOpen size={14} className="text-blue-400" />
                                <h4 className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">Teoría de Apertura</h4>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="text-sm font-black text-white leading-tight">
                                    {lastReview?.opening?.name || currentOpening?.name || "Apertura"}
                                </div>
                                <div className="text-[10px] font-bold text-blue-400/60 uppercase tracking-tighter">
                                    {(lastReview?.opening?.group || currentOpening?.groupName || "Teoría")}
                                </div>
                                {(lastReview?.opening?.commentary || currentOpening?.commentary) && (
                                    <div className="mt-3 p-3 bg-indigo-500/10 rounded-xl border border-indigo-500/20 italic text-[11px] text-white/90 leading-relaxed shadow-inner">
                                        "{lastReview?.opening?.commentary || currentOpening?.commentary}"
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Chat Panel */}
                    {isMultiplayer && (
                        <div className="flex-1 min-h-[300px] flex flex-col">
                            <ChatPanel
                                gameId={gameId || ''}
                                socket={socket}
                                currentUser={user}
                            />
                        </div>
                    )}

                    <div className="flex-1 shrink-0 min-h-0 bg-[#1a1c24]/40 lg:rounded-2xl flex flex-col overflow-hidden relative border-t lg:border border-white/5">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 shrink-0 bg-white/[0.01]">
                            <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Movimientos</h4>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                            {history.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center gap-6 opacity-10 py-24">
                                    <span className="text-xs font-black uppercase tracking-[0.5em]">Esperando jugada...</span>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    {Array.from({ length: Math.ceil(history.length / 2) }).map((_, i) => {
                                        const moveNum = i + 1;
                                        const wReview = reviewHistory[i * 2];
                                        const bReview = reviewHistory[i * 2 + 1];
                                        const wConfig = getBadgeConfig(wReview?.quality.label);
                                        const bConfig = getBadgeConfig(bReview?.quality.label);

                                        return (
                                            <div key={i} className="grid grid-cols-[30px_1fr_1fr] gap-3 items-center py-2 px-3 rounded-lg hover:bg-white/[0.04] transition-all group">
                                                <span className="text-[10px] font-black text-white/10 tabular-nums">{moveNum}.</span>

                                                {/* White Move Card */}
                                                <div className={`p-1.5 rounded-lg flex items-center justify-between transition-all duration-300 ${wConfig.bg} shadow-lg ring-1 ring-white/10`}>
                                                    <span className={`text-[11px] font-black tracking-wide ${wConfig.color}`}>
                                                        {history[i * 2] ? translateSanToSpanish(history[i * 2]) : ''}
                                                    </span>
                                                    {wConfig.symbol && (
                                                        <span className={`text-[9px] font-black ${wConfig.color} opacity-90`}>{wConfig.symbol}</span>
                                                    )}
                                                </div>

                                                {/* Black Move Card */}
                                                <div className={`p-1.5 rounded-lg flex items-center justify-between transition-all duration-300 ${bConfig.bg} shadow-lg ring-1 ring-white/10`}>
                                                    <span className={`text-[11px] font-black tracking-wide ${bConfig.color}`}>
                                                        {history[i * 2 + 1] ? (
                                                            translateSanToSpanish(history[i * 2 + 1])
                                                        ) : (
                                                            turn !== playerColor && i * 2 + 1 === history.length ? (
                                                                <span className="animate-pulse text-indigo-400/50">...</span>
                                                            ) : ''
                                                        )}
                                                    </span>
                                                    {bConfig.symbol && (
                                                        <span className={`text-[9px] font-black ${bConfig.color} opacity-90`}>{bConfig.symbol}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="hidden lg:grid grid-cols-2 gap-4 shrink-0 pb-4">
                        <button onClick={() => {
                            setConfirmOpts({
                                title: 'Salir',
                                message: '¿Seguro que deseas salir de la partida?',
                                onConfirm: () => {
                                    setGameState('config');
                                    setConfirmOpts(null);
                                }
                            });
                        }} className="py-5 rounded-[20px] bg-white/5 hover:bg-white/10 text-white font-black text-[11px] uppercase tracking-[0.25em] border border-white/5 transition-all active:scale-[0.97] flex items-center justify-center gap-2 shadow-lg">
                            <RotateCcw size={16} /> Salir
                        </button>
                        <button onClick={handleSurrender} disabled={isGameOver} className="py-5 rounded-[20px] bg-red-500/10 hover:bg-red-500/20 text-red-500 font-black text-[11px] uppercase tracking-[0.25em] border border-red-500/20 transition-all active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-20 shadow-lg">
                            <Flag size={16} /> Rendirse
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={!!confirmOpts}
                title={confirmOpts?.title || ''}
                message={confirmOpts?.message || ''}
                onConfirm={() => confirmOpts?.onConfirm()}
                onCancel={() => setConfirmOpts(null)}
                type="danger"
            />

            <GuestRestrictionModal
                isOpen={!!restrictionFeature}
                onClose={() => setRestrictionFeature(null)}
                featureName={restrictionFeature || ''}
            />
        </div>
    );
};

export default PlayPage;
